import { useCallback, useEffect, useState } from 'react';
import { LogManager } from 'Core/logManager';
import { COACH_SSO_LOG_KEY } from '../constants';

const LOG_MESSAGE = `${COACH_SSO_LOG_KEY} iframe load fired`;
const BLANK_DOCUMENT_URL = 'about:blank';

const STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
};

// What the parent can read about the embedded document. `contentDocument` is null for a
// cross-origin page, so `documentUrl` is only known when the browser lets us see it:
// same-origin pages, or the blank document the browser leaves when it refused to load the page.
export const readFrameDocument = (iframe) => {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) {
      return { readyState: null, documentUrl: null };
    }
    return { readyState: doc.readyState, documentUrl: doc.URL };
  } catch {
    return { readyState: null, documentUrl: null };
  }
};

// Tracks the Coaching iframe. The frame is "loading" until it fires `load`. When `load` fires and
// the document the parent can see is still `about:blank`, the Coaching page did not load (blocked
// or unreachable) and the view shows an error. There is no timeout and no retry.
export const useIframeLoadStatus = ({ src }) => {
  const [status, setStatus] = useState(STATUS.LOADING);

  useEffect(() => {
    setStatus(STATUS.LOADING);
  }, [src]);

  const onLoad = useCallback((event) => {
    const snapshot = readFrameDocument(event.target);
    const failed = snapshot.documentUrl === BLANK_DOCUMENT_URL;

    LogManager.info(LOG_MESSAGE, { ...snapshot, failed });

    setStatus(failed ? STATUS.FAILED : STATUS.LOADED);
  }, []);

  return {
    isLoaded: status === STATUS.LOADED,
    isFailed: status === STATUS.FAILED,
    onLoad,
  };
};
